import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  Award,
  Bot,
  CheckCircle2,
  ClipboardCheck,
  FileSearch,
  FileText,
  Gauge,
  Globe,
  Heart,
  Lightbulb,
  Mail,
  Printer,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Target,
  TrendingUp,
  XCircle,
} from 'lucide-react';
import { api } from '../api/client';
import { SkeletonHero, SkeletonGrid, SkeletonCard } from '../components/Skeleton';
import { formatDateTime, formatNumber, formatDate } from '../lib/format';
import { normalizeRecommendations } from '../lib/recommendations';

const PILLAR_META = {
  schemaMarkup: { label: 'Schema markup' },
  faqStructure: { label: 'FAQ structure' },
  answerCapsules: { label: 'Answer capsules' },
  headingHierarchy: { label: 'Heading hierarchy' },
  botAccess: { label: 'Bot access' },
  llmsTxt: { label: 'llms.txt' },
  contentQuality: { label: 'Content quality' },
};
const PILLAR_ORDER = [
  'schemaMarkup',
  'faqStructure',
  'answerCapsules',
  'headingHierarchy',
  'botAccess',
  'llmsTxt',
  'contentQuality',
];

const HEALTH_LABELS = {
  botsAllowed: 'AI bots allowed in robots.txt',
  schemaPresent: 'JSON-LD schema detected',
  faqsStructured: 'FAQ sections present',
  llmsTxtPresent: 'llms.txt published',
  answerCapsulesUsed: 'Answer Capsules used',
  headingHierarchyCorrect: 'Heading hierarchy correct',
  indexableByCrawlers: 'Indexable by AI crawlers',
};

const PRIORITY_TONE = {
  high: 'bg-rose-50 text-rose-700 border-rose-100',
  medium: 'bg-amber-50 text-amber-700 border-amber-100',
  low: 'bg-gray-50 text-gray-700 border-gray-100',
};

const scoreTone = (s) =>
  s == null ? 'gray' : s >= 75 ? 'green' : s >= 50 ? 'amber' : 'rose';

export default function Report({ mode = 'authed' }) {
  // mode='authed' uses /report/:id with the api client (sends auth header).
  // mode='public' uses /r/:slug → /public/reports/:slug (no auth).
  const params = useParams();
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const promise =
      mode === 'public'
        ? api.get(`/public/reports/${params.slug}`)
        : api.get(`/api/reports/${params.id}`);

    promise
      .then((r) => {
        setReport(r.data.report);
        setError('');
      })
      .catch((err) => {
        setError(err.response?.data?.error || 'Report not found');
      })
      .finally(() => setLoading(false));
  }, [params.slug, params.id, mode]);

  if (loading) {
    return (
      <div className="min-h-screen p-6">
        <div className="max-w-5xl mx-auto space-y-6">
          <SkeletonHero />
          <SkeletonGrid count={4} />
          <SkeletonCard lines={6} />
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="card max-w-md text-center">
          <div className="mx-auto mb-4 h-14 w-14 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center">
            <AlertTriangle className="w-7 h-7" />
          </div>
          <h2 className="text-lg font-semibold">Report unavailable</h2>
          <p className="text-sm text-gray-600 mt-2">
            {error || 'This report may have been deleted or made private.'}
          </p>
          {mode === 'authed' && (
            <Link to="/" className="btn-primary inline-flex mt-4">
              <ArrowLeft className="w-4 h-4" /> Back to dashboard
            </Link>
          )}
        </div>
      </div>
    );
  }

  return <ReportView report={report} mode={mode} />;
}

function ReportView({ report, mode }) {
  const { data, branding } = report;
  const primary = branding?.primaryColor || '#d61f97';
  const accent = branding?.accentColor || '#ffa23d';

  // Inline style with CSS variables so the report adapts to brand colors.
  const styleVars = {
    '--brand': primary,
    '--brand-accent': accent,
  };

  return (
    <div className="min-h-screen bg-white" style={styleVars}>
      <ReportToolbar report={report} mode={mode} />
      <div className="print-scorecard max-w-5xl mx-auto px-4 sm:px-8 py-8">
        <ReportHeader branding={branding} report={report} />
        <ExecutiveSummary data={data} />
        <AuditSection audit={data?.audit} />
        <ContentGapsSection gap={data?.contentGap} />
        <AnalyticsSection data={data} />
        <Recommendations audit={data?.audit} />
        <ReportFooter branding={branding} report={report} />
      </div>
    </div>
  );
}

function ReportToolbar({ report, mode }) {
  const [copied, setCopied] = useState(false);
  const publicUrl = report.is_public ? `${window.location.origin}/r/${report.slug}` : null;

  return (
    <div className="border-b border-gray-100 bg-white/80 backdrop-blur sticky top-0 z-10 print-hide">
      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm text-ink-400">
          {mode === 'authed' ? (
            <Link to="/" className="btn-ghost py-1.5">
              <ArrowLeft className="w-4 h-4" /> Dashboard
            </Link>
          ) : (
            <span className="text-xs">Shared snapshot · read-only</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {publicUrl && mode === 'authed' && (
            <button
              className="btn-secondary"
              onClick={async () => {
                await navigator.clipboard.writeText(publicUrl);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }}
            >
              {copied ? (
                <>
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Copied
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" /> Copy share link
                </>
              )}
            </button>
          )}
          <button className="btn-primary" onClick={() => window.print()}>
            <Printer className="w-4 h-4" /> Save as PDF
          </button>
        </div>
      </div>
    </div>
  );
}

function ReportHeader({ branding, report }) {
  const agency = branding?.agencyName || '';
  return (
    <header
      className="relative rounded-2xl overflow-hidden p-8 mb-8 text-white"
      style={{
        background:
          'linear-gradient(135deg, var(--brand) 0%, var(--brand-accent) 100%)',
      }}
    >
      <div className="absolute inset-0 bg-grid opacity-15" />
      <div className="relative flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          {branding?.logoUrl && (
            <img
              src={branding.logoUrl}
              alt={agency}
              className="h-10 w-auto mb-4 bg-white/95 rounded-lg p-1.5"
              onError={(e) => (e.target.style.display = 'none')}
            />
          )}
          <div className="text-xs uppercase tracking-wider opacity-80 mb-1">
            {agency || 'AEO Audit Report'}
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold leading-tight">
            {report.title}
          </h1>
          {branding?.tagline && (
            <p className="mt-2 text-white/85 max-w-xl">{branding.tagline}</p>
          )}
          <div className="mt-4 inline-flex items-center gap-2 text-sm text-white/85">
            <Globe className="w-4 h-4" />
            <span className="font-medium">{report.data?.domain?.domain}</span>
            <span className="opacity-60">·</span>
            <span>Generated {formatDateTime(report.createdAt)}</span>
          </div>
        </div>
      </div>
    </header>
  );
}

function ExecutiveSummary({ data }) {
  const score = data?.audit?.avg_score;
  const tone = scoreTone(score);
  const ringColor =
    tone === 'green' ? '#10b981' : tone === 'amber' ? '#f59e0b' : tone === 'rose' ? '#f43f5e' : '#9ca3af';
  const radius = 52;
  const circ = 2 * Math.PI * radius;
  const offset = score != null ? circ * (1 - score / 100) : circ;

  return (
    <section className="mb-10">
      <SectionLabel icon={Sparkles}>Executive summary</SectionLabel>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="rounded-2xl border border-gray-100 p-6 flex items-center gap-5">
          <svg width="120" height="120" viewBox="0 0 120 120" className="flex-none">
            <circle cx="60" cy="60" r={radius} stroke="#f1f3f7" strokeWidth="10" fill="none" />
            <circle
              cx="60"
              cy="60"
              r={radius}
              stroke={ringColor}
              strokeWidth="10"
              fill="none"
              strokeLinecap="round"
              strokeDasharray={circ}
              strokeDashoffset={offset}
              transform="rotate(-90 60 60)"
            />
            <text x="60" y="58" textAnchor="middle" fontSize="28" fontWeight="700" fill="#0f172a">
              {score != null ? score : '—'}
            </text>
            <text x="60" y="78" textAnchor="middle" fontSize="9" fill="#71748a" letterSpacing="1.5">
              GEO SCORE
            </text>
          </svg>
          <div>
            <div className="text-xs uppercase tracking-wider text-ink-400 font-semibold mb-1">
              Site GEO score
            </div>
            <div className="text-sm text-ink-700">
              {score == null
                ? 'No audit data available.'
                : score >= 75
                  ? 'Strong AI-citation readiness.'
                  : score >= 50
                    ? 'Several improvements would lift this significantly.'
                    : 'Substantial gaps in AI-readiness.'}
            </div>
            {data?.audit?.createdAt && (
              <div className="text-xs text-ink-400 mt-2">
                Audited {formatDate(data.audit.createdAt)}
              </div>
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-gray-100 p-6 lg:col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-4">
          <SummaryStat
            label="AI referrals · 7d"
            value={formatNumber(data?.aiReferrals?.last7 || 0)}
            icon={TrendingUp}
          />
          <SummaryStat
            label="Bot visits · 30d"
            value={formatNumber(data?.botActivity?.last30Visits || 0)}
            icon={Bot}
          />
          <SummaryStat
            label="Pages optimized"
            value={`${data?.pages?.optimized || 0}/${data?.pages?.total || 0}`}
            icon={FileText}
          />
          <SummaryStat
            label="Content gaps"
            value={data?.contentGap?.gaps?.length || 0}
            icon={Lightbulb}
          />
        </div>
      </div>
    </section>
  );
}

function SummaryStat({ label, value, icon: Icon }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-ink-400 font-semibold mb-1">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="text-2xl font-bold text-ink-900">{value}</div>
    </div>
  );
}

function AuditSection({ audit }) {
  if (!audit) {
    return (
      <section className="mb-10">
        <SectionLabel icon={ClipboardCheck}>GEO audit</SectionLabel>
        <div className="rounded-2xl border border-gray-100 p-6 text-sm text-ink-400">
          No audit has been run yet for this domain.
        </div>
      </section>
    );
  }

  return (
    <section className="mb-10">
      <SectionLabel icon={ClipboardCheck}>GEO audit</SectionLabel>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <MetricTile icon={Award} label="Site score" value={`${audit.avg_score}/100`} />
        <MetricTile icon={FileSearch} label="Pages scanned" value={audit.total_pages_scanned} />
        <MetricTile icon={XCircle} label="Missing schema" value={audit.missing_schema_count} />
        <MetricTile icon={XCircle} label="Missing FAQs" value={audit.missing_faq_count} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-gray-100 p-5">
          <h3 className="font-semibold mb-3 text-ink-900 flex items-center gap-2">
            <Gauge className="w-4 h-4 text-[color:var(--brand)]" /> Pillar breakdown
          </h3>
          <ul className="space-y-2.5">
            {PILLAR_ORDER.map((key) => {
              const v = audit.pillars?.[key] ?? 0;
              const max = audit.pillar_max?.[key] || 1;
              const pct = Math.min(100, Math.round((v / max) * 100));
              return (
                <li key={key}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-ink-700 font-medium">
                      {PILLAR_META[key].label}
                    </span>
                    <span className="text-ink-400">
                      <span className="text-ink-900 font-semibold">{v}</span> / {max}
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${pct}%`,
                        background:
                          'linear-gradient(90deg, var(--brand), var(--brand-accent))',
                      }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="rounded-2xl border border-gray-100 p-5">
          <h3 className="font-semibold mb-3 text-ink-900 flex items-center gap-2">
            <Heart className="w-4 h-4 text-[color:var(--brand)]" /> Site health
          </h3>
          <ul className="text-sm divide-y divide-gray-50">
            {Object.entries(HEALTH_LABELS).map(([k, label]) => {
              const passed = audit.health_checks?.[k];
              return (
                <li
                  key={k}
                  className="py-2 flex items-center justify-between first:pt-0 last:pb-0"
                >
                  <span className="text-ink-800">{label}</span>
                  {passed ? (
                    <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-semibold">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Pass
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-rose-600 text-xs font-semibold">
                      <XCircle className="w-3.5 h-3.5" /> Fail
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {audit.worst_pages?.length > 0 && (
        <div className="rounded-2xl border border-gray-100 p-5 mt-4">
          <h3 className="font-semibold mb-3 text-ink-900 flex items-center gap-2">
            <Target className="w-4 h-4 text-[color:var(--brand)]" /> Lowest-scoring pages
          </h3>
          <ul className="text-sm divide-y divide-gray-50">
            {audit.worst_pages.slice(0, 8).map((p, i) => (
              <li
                key={i}
                className="py-2 flex items-center justify-between gap-3 first:pt-0 last:pb-0"
              >
                <span className="truncate text-ink-800 flex-1 font-mono text-xs">
                  {p.url}
                </span>
                <span
                  className={`font-semibold text-xs ${
                    p.score >= 75
                      ? 'text-emerald-700'
                      : p.score >= 50
                        ? 'text-amber-700'
                        : 'text-rose-700'
                  }`}
                >
                  {p.score} / 100
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function ContentGapsSection({ gap }) {
  if (!gap || !gap.gaps?.length) return null;

  const high = gap.gaps.filter((g) => g.priority === 'high');
  const medium = gap.gaps.filter((g) => g.priority === 'medium');
  const low = gap.gaps.filter((g) => g.priority === 'low');

  return (
    <section className="mb-10">
      <SectionLabel icon={Lightbulb}>Content opportunities</SectionLabel>
      <div className="rounded-2xl border border-gray-100 p-5 mb-3 flex items-center gap-3 flex-wrap text-sm text-ink-700">
        <span>
          <span className="font-semibold">{gap.gaps.length}</span> opportunities identified
        </span>
        {gap.business_type && (
          <>
            <span className="text-ink-400">·</span>
            <span>{gap.business_type}</span>
          </>
        )}
        {gap.industry && (
          <>
            <span className="text-ink-400">·</span>
            <span>{gap.industry}</span>
          </>
        )}
      </div>
      {[
        { items: high, label: 'High priority' },
        { items: medium, label: 'Medium priority' },
        { items: low, label: 'Low priority' },
      ].map(
        ({ items, label }) =>
          items.length > 0 && (
            <div key={label} className="mb-5 last:mb-0">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-ink-400 mb-2">
                {label}
              </h4>
              <ul className="space-y-2">
                {items.map((g, i) => (
                  <li
                    key={i}
                    className={`rounded-xl border p-4 ${PRIORITY_TONE[g.priority]}`}
                  >
                    <div className="font-semibold text-ink-900">{g.question}</div>
                    {g.rationale && (
                      <p className="text-sm mt-1.5 text-ink-700 leading-relaxed">
                        {g.rationale}
                      </p>
                    )}
                    {(g.suggested_title || g.suggested_url) && (
                      <div className="mt-2 text-xs text-ink-400 flex gap-4 flex-wrap">
                        {g.suggested_title && (
                          <span>
                            <strong className="text-ink-700">Suggested title:</strong>{' '}
                            {g.suggested_title}
                          </span>
                        )}
                        {g.suggested_url && (
                          <span className="font-mono">
                            <strong className="text-ink-700">Path:</strong>{' '}
                            {g.suggested_url}
                          </span>
                        )}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ),
      )}
    </section>
  );
}

function AnalyticsSection({ data }) {
  return (
    <section className="mb-10">
      <SectionLabel icon={TrendingUp}>30-day analytics</SectionLabel>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricTile
          label="AI referrals · 30d"
          value={formatNumber(data?.aiReferrals?.last30 || 0)}
          icon={TrendingUp}
        />
        <MetricTile
          label="AI referrals · 7d"
          value={formatNumber(data?.aiReferrals?.last7 || 0)}
          icon={Sparkles}
        />
        <MetricTile
          label="Bot visits · 30d"
          value={formatNumber(data?.botActivity?.last30Visits || 0)}
          icon={Bot}
        />
        <MetricTile
          label="Pages crawled"
          value={formatNumber(data?.botActivity?.last30Pages || 0)}
          icon={FileText}
        />
      </div>

      <div className="rounded-2xl border border-gray-100 p-5 mt-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          {data?.domain?.verified ? (
            <span className="inline-flex items-center gap-1.5 text-emerald-700 font-semibold text-sm">
              <ShieldCheck className="w-4 h-4" /> Domain verified
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-amber-700 font-semibold text-sm">
              <ShieldAlert className="w-4 h-4" /> Domain not yet verified
            </span>
          )}
        </div>
        <div className="text-xs text-ink-400">
          Data covers the 30 days prior to {formatDate(data?.generatedAt)}
        </div>
      </div>
    </section>
  );
}

function Recommendations({ audit }) {
  const recs = normalizeRecommendations(audit?.recommendations);
  if (!recs.length) return null;

  return (
    <section className="mb-10">
      <SectionLabel icon={CheckCircle2}>Recommended next steps</SectionLabel>
      <ol className="space-y-3">
        {recs.map((r, i) => (
          <li
            key={i}
            className="flex items-start gap-3 rounded-2xl border border-gray-100 p-4"
          >
            <span
              className="flex-none h-7 w-7 rounded-lg text-white text-xs font-bold flex items-center justify-center"
              style={{
                background:
                  'linear-gradient(135deg, var(--brand), var(--brand-accent))',
              }}
            >
              {i + 1}
            </span>
            <p className="text-sm text-ink-800 leading-relaxed">{r.text}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

function ReportFooter({ branding, report }) {
  return (
    <footer className="border-t border-gray-100 pt-6 text-xs text-ink-400 flex items-center justify-between gap-4 flex-wrap">
      <div>
        {branding?.footerNote && (
          <div className="text-ink-700 mb-1">{branding.footerNote}</div>
        )}
        <div>Generated {formatDateTime(report.createdAt)}</div>
      </div>
      <div className="flex items-center gap-4">
        {branding?.contactEmail && (
          <a
            href={`mailto:${branding.contactEmail}`}
            className="inline-flex items-center gap-1 hover:text-[color:var(--brand)]"
          >
            <Mail className="w-3 h-3" />
            {branding.contactEmail}
          </a>
        )}
        {branding?.contactUrl && (
          <a
            href={branding.contactUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 hover:text-[color:var(--brand)]"
          >
            <Globe className="w-3 h-3" />
            {branding.contactUrl.replace(/^https?:\/\//, '')}
          </a>
        )}
      </div>
    </footer>
  );
}

function SectionLabel({ icon: Icon, children }) {
  return (
    <h2 className="text-xl font-bold text-ink-900 mb-4 flex items-center gap-2 print-keep-with-next">
      <Icon className="w-5 h-5 text-[color:var(--brand)]" />
      {children}
    </h2>
  );
}

function MetricTile({ label, value, icon: Icon }) {
  return (
    <div className="rounded-2xl border border-gray-100 p-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-ink-400 font-semibold mb-1.5">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="text-2xl font-bold text-ink-900">{value}</div>
    </div>
  );
}
